import { nextTick, readonly, shallowRef } from 'vue';

/**
 * 配置驱动的界面引导，拥有步骤导航、本地记录及浮层定位的生命周期。
 * 一个页面拥有一个实例，视图挂载后 attach，页面卸载时 dispose；不执行 CAD 命令。
 */
export class OnBoarding {
  /** 经校验并复制的配置，外部修改原数组不会改变运行中的步骤。 */
  readonly config: OnBoardingConfig;
  private readonly snapshot = shallowRef<OnBoardingState>({
    active: false, busy: false, index: 0, steps: [], entryFailed: false, rect: null, cardStyle: {}, error: '',
  });
  /** 供 Vue 视图消费的只读状态。 */
  readonly state = readonly(this.snapshot);
  private dialog: HTMLDialogElement | null = null;
  private card: HTMLElement | null = null;
  private target: Element | null = null;
  private observer: ResizeObserver | null = null;
  private lifecycle: AbortController | null = null;
  private stepScope: OnBoardingStepScope | null = null;
  private frame: number | null = null;
  private disposed = false;
  private dismissed = false;
  private readonly capturedKeys = new Set<string>();

  /**
   * 校验配置并保留独立副本。
   * @param config 非空步骤列表及本地记录、样式设置。
   */
  constructor(config: OnBoardingConfig) {
    if (!config.storageKey?.trim() || !Array.isArray(config.tips) || !config.tips.length) {
      throw new Error('[OnBoarding] storageKey 和非空 tips 必须配置');
    }
    if (config.backgroundOpacity !== undefined
      && (!Number.isFinite(config.backgroundOpacity) || config.backgroundOpacity < 0 || config.backgroundOpacity > 1)) {
      throw new Error('[OnBoarding] backgroundOpacity 必须介于 0 和 1');
    }
    const tips = config.tips.map((step, index) => {
      if (!step || typeof step.el !== 'string' || !step.el.trim() || typeof step.content !== 'string'
        || (step.title !== undefined && typeof step.title !== 'string')
        || (step.tipPosition !== undefined && !['top', 'bottom', 'left', 'right'].includes(step.tipPosition))
        || (step.onNext !== undefined && typeof step.onNext !== 'function')
        || (step.onPrev !== undefined && typeof step.onPrev !== 'function')) {
        throw new Error(`[OnBoarding] 第 ${index + 1} 步配置无效`);
      }
      if ((step.when !== undefined && typeof step.when !== 'function')
        || (step.onEnter !== undefined && typeof step.onEnter !== 'function')) {
        throw new Error(`[OnBoarding] 第 ${index + 1} 步生命周期配置无效`);
      }
      // 选择器语法错误属于配置问题；元素暂未出现则允许居中展示说明。
      try {
        document.querySelector(step.el);
      } catch (error) {
        throw new Error(`[OnBoarding] 第 ${index + 1} 步选择器无效：${step.el}`, { cause: error });
      }
      return Object.freeze({ ...step });
    });
    this.config = Object.freeze({
      ...config,
      tips: Object.freeze(tips),
      titleStyle: Object.freeze({ ...config.titleStyle }),
      contentStyle: Object.freeze({ ...config.contentStyle }),
    });
    this.snapshot.value = { ...this.snapshot.value, steps: this.config.tips };
    // 必须早于 Viewer 与命令的捕获监听注册，否则 Enter/Esc 会先修改建模状态。
    window.addEventListener('keydown', this.handleKeyboard, true);
    window.addEventListener('keyup', this.handleKeyboard, true);
    window.addEventListener('blur', this.clearCapturedKeys);
  }

  /**
   * 绑定由 Vue 创建的视图节点。
   * @param dialog 原生模态对话框。
   * @param card 内容卡片，用于测量实际高度。
   */
  attach(dialog: HTMLDialogElement, card: HTMLElement): void {
    if (this.disposed || this.dialog) throw new Error('[OnBoarding] 实例已销毁或已绑定视图');
    this.dialog = dialog;
    this.card = card;
  }

  /**
   * 仅在未完成且未跳过时自动开始；存储不可用时仍允许当前页面正常引导。
   * @returns 是否成功启动。
   */
  async startIfNeeded(): Promise<boolean> {
    if (this.dismissed || this.disposed || this.snapshot.value.active) return false;
    try {
      const record = window.localStorage.getItem(this.config.storageKey);
      if (record === 'completed' || record === 'skipped') return false;
    } catch (error) {
      console.warn('[OnBoarding] 读取本地引导记录失败', this.config.storageKey, error);
    }
    return this.start();
  }

  /**
   * 从第一步重新开始，忽略历史完成记录；重复调用不会重启正在运行的引导。
   * @returns 是否成功启动。
   */
  async start(): Promise<boolean> {
    if (this.disposed || this.snapshot.value.active) return false;
    if (!this.dialog || !this.card) throw new Error('[OnBoarding] 启动前必须绑定视图');
    let steps: readonly OnBoardingStep[];
    try {
      steps = this.config.tips.filter((step) => step.when?.() !== false);
    } catch (error) {
      console.error('[OnBoarding] 判断步骤适用条件失败', this.config.storageKey, error);
      return false;
    }
    if (!steps.length) return false;
    const lifecycle = new AbortController();
    this.lifecycle = lifecycle;
    this.snapshot.value = { ...this.snapshot.value, active: true, index: 0, steps, entryFailed: false, error: '', rect: null };
    await nextTick();
    if (lifecycle.signal.aborted) return false;
    try {
      // 浏览器顶层模态提供焦点约束和背景 inert，无需改写编辑器 DOM 的层级或样式。
      this.dialog.showModal();
      this.observer = new ResizeObserver(this.scheduleLayout);
      this.observer.observe(this.card);
      this.observer.observe(document.documentElement);
      window.addEventListener('resize', this.scheduleLayout);
      window.addEventListener('scroll', this.scheduleLayout, true);
      await this.enterStep(0, lifecycle);
      if (lifecycle.signal.aborted) return false;
      return true;
    } catch (error) {
      this.close();
      console.error('[OnBoarding] 启动引导失败', this.config.storageKey, error);
      return false;
    }
  }

  /** 前往下一步，最后一步完成引导；返回值表示导航是否成功。 */
  next(): Promise<boolean> {
    return this.navigate(1);
  }

  /** 返回上一步，第一步不回退；返回值表示导航是否成功。 */
  prev(): Promise<boolean> {
    return this.navigate(-1);
  }

  /** 重试失败的步骤准备；返回值表示界面是否已就绪。 */
  async retry(): Promise<boolean> {
    if (!this.lifecycle || this.snapshot.value.busy || !this.snapshot.value.entryFailed) return false;
    return this.enterStep(this.snapshot.value.index, this.lifecycle);
  }

  /** 主动跳过并记住选择，取消尚未完成的异步导航。 */
  skip(): void {
    if (this.snapshot.value.active) this.finish('skipped');
  }

  /** 关闭当前引导但不记录完成，适用于外部关闭或页面暂时退出。 */
  close(): void {
    this.lifecycle?.abort();
    this.lifecycle = null;
    this.releaseStep(this.disposed ? 'dispose' : 'close');
    this.observer?.disconnect();
    this.observer = null;
    this.target = null;
    if (this.frame !== null) cancelAnimationFrame(this.frame);
    this.frame = null;
    window.removeEventListener('resize', this.scheduleLayout);
    window.removeEventListener('scroll', this.scheduleLayout, true);
    this.snapshot.value = { ...this.snapshot.value, active: false, busy: false, entryFailed: false, rect: null, error: '' };
    if (this.dialog?.open) this.dialog.close();
  }

  /** 释放全部监听、观察器和节点引用；可重复调用，销毁后不可重用。 */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.close();
    window.removeEventListener('keydown', this.handleKeyboard, true);
    window.removeEventListener('keyup', this.handleKeyboard, true);
    window.removeEventListener('blur', this.clearCapturedKeys);
    this.clearCapturedKeys();
    this.dialog = null;
    this.card = null;
  }

  /**
   * 串行执行导航钩子，退出后旧回调无权改变下一次引导。
   * @param direction 导航方向。
   * @returns 是否推进或完成。
   */
  private async navigate(direction: -1 | 1): Promise<boolean> {
    const { active, busy, index } = this.snapshot.value;
    const lifecycle = this.lifecycle;
    if (!active || busy || !lifecycle || (index === 0 && direction === -1)) return false;
    const step = this.snapshot.value.steps[index];
    const callback = direction === 1 ? step.onNext : step.onPrev;
    this.snapshot.value = { ...this.snapshot.value, busy: true, error: '' };
    try {
      const result = await callback?.({ signal: lifecycle.signal, index });
      if (lifecycle.signal.aborted || result === false) return false;
      if (index + direction === this.snapshot.value.steps.length) {
        this.finish('completed');
      } else {
        this.releaseStep('leave');
        return await this.enterStep(index + direction, lifecycle);
      }
      return true;
    } catch (error) {
      if (!lifecycle.signal.aborted) {
        console.error('[OnBoarding] 步骤导航失败', { index, selector: step.el, direction }, error);
        this.snapshot.value = { ...this.snapshot.value, error: '此步骤未能完成，请重试或跳过引导。' };
      }
      return false;
    } finally {
      if (!lifecycle.signal.aborted) this.snapshot.value = { ...this.snapshot.value, busy: false };
    }
  }

  /**
   * 为一个步骤建立独立可取消作用域，准备成功后才测量实际展开的目标。
   * @param index 目标步骤索引。
   * @param lifecycle 本轮引导，用于阻止旧准备任务恢复后污染重播。
   * @returns 目标界面是否准备成功。
   */
  private async enterStep(index: number, lifecycle: AbortController): Promise<boolean> {
    const step = this.snapshot.value.steps[index];
    const scope: OnBoardingStepScope = { controller: new AbortController(), cleanups: [], ready: false };
    this.stepScope = scope;
    this.snapshot.value = {
      ...this.snapshot.value, index, busy: true, entryFailed: false, error: '', rect: null,
      cardStyle: { left: '50%', top: '50%', transform: 'translate(-50%, -50%)' },
    };
    try {
      await step.onEnter?.({
        signal: scope.controller.signal,
        index,
        // 登记后即归步骤所有，等待中退出也能同步恢复已经展开的面板。
        onCleanup: (cleanup) => {
          if (scope.controller.signal.aborted) cleanup(scope.controller.signal.reason as OnBoardingLeaveReason);
          else scope.cleanups.push(cleanup);
        },
      });
      await nextTick();
      if (lifecycle.signal.aborted || scope.controller.signal.aborted) return false;
      scope.ready = true;
      this.updateLayout();
      return true;
    } catch (error) {
      if (!lifecycle.signal.aborted && !scope.controller.signal.aborted) {
        this.releaseStep('error');
        console.error('[OnBoarding] 准备引导步骤失败', { index, selector: step.el }, error);
        this.snapshot.value = { ...this.snapshot.value, entryFailed: true, error: '此步骤的界面未能准备完成，请重试或继续查看其他步骤。' };
      }
      return false;
    } finally {
      if (!lifecycle.signal.aborted && this.snapshot.value.index === index) {
        this.snapshot.value = { ...this.snapshot.value, busy: false };
      }
    }
  }

  /**
   * 取消步骤准备并同步恢复临时界面；单个恢复失败不能阻止其它资源释放。
   * @param reason 离开原因。
   */
  private releaseStep(reason: OnBoardingLeaveReason): void {
    const scope = this.stepScope;
    if (!scope) return;
    this.stepScope = null;
    scope.controller.abort(reason);
    while (scope.cleanups.length) {
      const cleanup = scope.cleanups.pop()!;
      try {
        cleanup(reason);
      } catch (error) {
        console.error('[OnBoarding] 恢复步骤界面失败', { index: this.snapshot.value.index, reason }, error);
      }
    }
  }

  /**
   * 记录主动结束；写入失败也必须释放模态，避免妨碍编辑器使用。
   * @param result 完成或主动跳过。
   */
  private finish(result: 'completed' | 'skipped'): void {
    this.dismissed = true;
    try {
      window.localStorage.setItem(this.config.storageKey, result);
    } catch (error) {
      console.warn('[OnBoarding] 保存本地引导记录失败', this.config.storageKey, error);
    } finally {
      this.close();
    }
  }

  /**
   * 阻断引导内发起的 CAD 快捷键，同时允许引导前按下的键释放相机状态。
   * @param event 键盘事件。
   */
  private readonly handleKeyboard = (event: KeyboardEvent): void => {
    const key = event.code || event.key;
    if (event.type === 'keyup') {
      // Enter 可能已关闭浮层并恢复输入框焦点，配对的 keyup 仍不能提交背景属性。
      if (this.capturedKeys.delete(key)) event.stopImmediatePropagation();
      return;
    }
    if (!this.snapshot.value.active) {
      // 长按 Enter/Esc 关闭后，重复按键仍属于引导，直到释放才交还给编辑器。
      if (event.repeat && this.capturedKeys.has(key)) {
        event.stopImmediatePropagation();
        event.preventDefault();
      } else if (!event.repeat) {
        this.capturedKeys.delete(key);
      }
      return;
    }
    if (!event.repeat) this.capturedKeys.add(key);
    event.stopImmediatePropagation();
    const plainKey = !event.ctrlKey && !event.metaKey && !event.altKey;
    if (plainKey && ['Tab', 'Enter', ' '].includes(event.key)) return;
    event.preventDefault();
    if (event.repeat || !plainKey) return;
    if (event.key === 'Escape') this.skip();
    else if (event.key === 'ArrowRight') void this.next();
    else if (event.key === 'ArrowLeft') void this.prev();
  };

  /** 浏览器失焦可能不再发送配对 keyup，清除记录避免影响下一次真实输入。 */
  private readonly clearCapturedKeys = (): void => {
    this.capturedKeys.clear();
  };

  /** 将尺寸和滚动事件合并到一次布局测量，关闭后不保留动画循环。 */
  private readonly scheduleLayout = (): void => {
    if (!this.snapshot.value.active || this.frame !== null) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = null;
      this.updateLayout();
    });
  };

  /** 测量当前可见目标并在视口内定位卡片；缺失目标使用居中说明，不等待轮询。 */
  private updateLayout(): void {
    if (!this.snapshot.value.active || !this.card) return;
    if (this.snapshot.value.entryFailed || (this.stepScope && !this.stepScope.ready)) return;
    const step = this.snapshot.value.steps[this.snapshot.value.index];
    const target = document.querySelector(step.el);
    if (target !== this.target) {
      if (this.target) this.observer?.unobserve(this.target);
      this.target = target;
      if (target) this.observer?.observe(target);
    }
    const width = document.documentElement.clientWidth || window.innerWidth;
    const height = document.documentElement.clientHeight || window.innerHeight;
    const bounds = target?.getBoundingClientRect();
    let rect: OnBoardingRect | null = null;
    if (bounds && bounds.width > 0 && bounds.height > 0 && bounds.right > 0 && bounds.bottom > 0
      && bounds.left < width && bounds.top < height && getComputedStyle(target!).visibility !== 'hidden') {
      const left = Math.max(0, bounds.left - 4);
      const top = Math.max(0, bounds.top - 4);
      rect = { left, top, width: Math.min(width, bounds.right + 4) - left, height: Math.min(height, bounds.bottom + 4) - top };
    }
    const cardWidth = this.card.offsetWidth;
    const cardHeight = this.card.offsetHeight;
    const margin = 12;
    let left = (width - cardWidth) / 2;
    let top = (height - cardHeight) / 2;
    if (rect) {
      const right = rect.left + rect.width;
      const bottom = rect.top + rect.height;
      let position = step.tipPosition ?? 'bottom';
      // 优先翻转到相对侧；两侧都不足时仍将卡片约束在可用视口内。
      if (position === 'bottom' && height - bottom < cardHeight + margin * 2 && rect.top > height - bottom) position = 'top';
      else if (position === 'top' && rect.top < cardHeight + margin * 2 && height - bottom > rect.top) position = 'bottom';
      else if (position === 'left' && rect.left < cardWidth + margin * 2 && width - right > rect.left) position = 'right';
      else if (position === 'right' && width - right < cardWidth + margin * 2 && rect.left > width - right) position = 'left';
      left = position === 'left' ? rect.left - cardWidth - margin
        : position === 'right' ? right + margin : rect.left + (rect.width - cardWidth) / 2;
      top = position === 'top' ? rect.top - cardHeight - margin
        : position === 'bottom' ? bottom + margin : rect.top + (rect.height - cardHeight) / 2;
    }
    const cardStyle = {
      left: `${Math.max(margin, Math.min(left, width - cardWidth - margin))}px`,
      top: `${Math.max(margin, Math.min(top, height - cardHeight - margin))}px`,
    };
    this.snapshot.value = { ...this.snapshot.value, rect, cardStyle };
  }
}
